import { useEffect, useState } from "react";
import { useUpdateEffect } from "react-use";
import { useMutation } from "react-query";
import Modal from "react-modal";
import { v4 as uuidv4 } from "uuid";
import SurveyView from "./SurveyView";
import { Button, ErrorAlert, SuccessAlert } from "../common/Forms";
import Icon from "../common/Icon";
import { deleteSurvey } from "../../api";

const SurveyViewModal = ({
  open,
  data: d,
  auth,
  loading,
  result,
  error,
  onClose,
  onUpdate,
  onDuplicate,
  onUpload,
}) => {
  const [target, setTarget] = useState("parent");
  const [mode, setMode] = useState(null);
  const [page, setPage] = useState(0);
  const [dupData, setDupdata] = useState(null);
  const [files, setFile] = useState(null);
  const [data, setData] = useState(null);

  const deleteMttn = useMutation({
    mutationFn: deleteSurvey,
    onSettled: (res) => {
      const { result, error } = res;
      if (result) {
        setData({ ...data, [target]: null });
      }
      if (error) {
        alert(error);
      }
    },
  });

  const handleClick = (e) => {
    setTarget(e.target.id);
  };
  const next = () => {
    setPage((v) => v + 1);
  };
  const prev = () => {
    setMode(null);
    setFile(null);
    setPage((v) => v - 1);
  };

  const handleDuplicate = () => {
    if (page === 0) {
      setMode("duplicate");
      const { title, contents, questions, options } = data[target];
      const list = questions.reduce((acc, cur, i) => {
        const id = uuidv4();
        return [...acc, { id, key: id, q: cur, options: options[i] }];
      }, []);
      setDupdata({ data: { title, contents, list }, target, lecture_id: null });
      next();
    }
    if (page === 1) {
      onDuplicate(dupData);
    }
  };

  const handleDelete = () => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm("이 설문을 삭제하시겠습니까?")) {
      deleteMttn.mutate(data[target]._id);
    }
  };

  const handleDupTarget = (e) => {
    setDupdata({ ...dupData, target: e.target.id });
  };

  const handleSelect = (e) => {
    setDupdata({ ...dupData, lecture_id: e.target.value });
  };

  const handleFileChange = (e) => {
    setFile(e.target.files);
  };

  const handleUpload = () => {
    if (page === 0) {
      setMode("upload");
      next();
    }
    if (page === 1) {
      onUpload({ id: data[target]._id, files });
    }
  };

  useUpdateEffect(() => {
    if (!open) {
      setTarget("parent");
      setMode(null);
      setPage(0);
      setDupdata(null);
      setFile(null);
    }
  }, [open]);

  useEffect(() => {
    setData(d);
    if (d && "student" in d && d.student) {
      setTarget("student");
    }
  }, [d]);

  return (
    <>
      {open && data && (
        <Modal
          ariaHideApp={false}
          className="h-full text-center flex"
          isOpen={open}
          onRequestClose={onClose}
        >
          <div className="card bg-white w-full h-full m-auto md:w-9/12 md:shadow-xl md:min-h-[50vh] md:max-h-[83.4vh]">
            {page === 0 && (
              <div className="card-body overflow-hidden scale-90 m-[-2rem]">
                <div className="btn-group m-2 w-full justify-center">
                  <button
                    id="parent"
                    className={`btn w-2/5 btn-ghost border border-zinc-200 ${
                      target === "parent" && "btn-active"
                    }`}
                    onClick={handleClick}
                  >
                    학부모
                  </button>
                  <button
                    id="student"
                    className={`btn w-2/5 btn-ghost border border-zinc-200 ${
                      target === "student" && "btn-active"
                    }`}
                    onClick={handleClick}
                  >
                    학생
                  </button>
                </div>
                <div className="text-start h-full overflow-scroll scrollbar-hide border border-zinc-200 rounded-md">
                  {(!(target in data) || !data[target]) && (
                    <div className="w-full text-center my-4">
                      등록된 설문이 없습니다.
                    </div>
                  )}
                  {target in data && data[target] && (
                    <SurveyView {...data[target]} />
                  )}
                </div>
                <div className="card-actions mt-4 w-full">
                  <div className="inline-flex gap-2 w-full">
                    <Button
                      type="primary"
                      outlined
                      className="flex-auto"
                      disabled={!(target in data && data[target])}
                      onClick={handleUpload}
                    >
                      <Icon
                        name="arrow-up-tray"
                        size="sm"
                        className="inline-block"
                      />
                      결과 업로드
                    </Button>
                    <Button
                      type="accent"
                      className="flex-auto"
                      disabled={!(target in data && data[target])}
                      onClick={() =>
                        target in data &&
                        data[target] &&
                        onUpdate(data[target]._id, target)
                      }
                    >
                      <Icon
                        name="pencil-square"
                        size="sm"
                        className="inline-block"
                      />
                      수정
                    </Button>
                    {auth && (
                      <Button
                        type="error"
                        className="flex-auto"
                        disabled={!(target in data && data[target])}
                        onClick={handleDelete}
                      >
                        <Icon
                          name="pencil-square"
                          size="sm"
                          className="inline-block"
                        />
                        삭제
                      </Button>
                    )}
                    <Button
                      type="primary"
                      className="flex-auto"
                      disabled={!(target in data && data[target])}
                      onClick={handleDuplicate}
                    >
                      <Icon
                        name="document-duplicate"
                        size="sm"
                        className="inline-block"
                      />
                      복제
                    </Button>
                  </div>
                  <Button
                    className="w-full bg-zinc-300 border-0 text-black hover:bg-zinc-400"
                    onClick={onClose}
                  >
                    <Icon name="x" size="sm" className="inline-block" />
                    닫기
                  </Button>
                </div>
              </div>
            )}
            {page === 1 && mode === "duplicate" && (
              <div className="card-body overflow-hidden scale-90 m-[-2rem]">
                <div className="text-start h-full border border-zinc-200 rounded-md flex flex-col p-4 gap-4 flex-1">
                  <div className="inline-flex gap-4 items-center">
                    <h3 className="text-sm font-bold w-12 text-end">대상</h3>
                    <div className="btn-group w-full justify-center">
                      <button
                        id="parent"
                        className={`btn btn-sm w-1/2 btn-ghost border border-zinc-200 ${
                          dupData.target === "parent" && "btn-active"
                        }`}
                        onClick={handleDupTarget}
                      >
                        학부모
                      </button>
                      <button
                        id="student"
                        className={`btn btn-sm w-1/2 btn-ghost border border-zinc-200 ${
                          dupData.target === "student" && "btn-active"
                        }`}
                        onClick={handleDupTarget}
                      >
                        학생
                      </button>
                    </div>
                  </div>
                  <div className="inline-flex gap-4 items-center">
                    <h3 className="text-sm font-bold w-12 text-end">강의</h3>
                    <select
                      id="classes"
                      name="classes"
                      autoComplete="classes-name"
                      className="h-12 mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
                      onChange={handleSelect}
                    >
                      {data.lectureList.map(({ _id: id, title: v }) => (
                        <option key={`option_${v}`} value={id}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="card-actions mt-4 w-full">
                  <div className="inline-flex gap-2 w-full">
                    <Button
                      className="flex-auto bg-zinc-300 border-0 text-black hover:bg-zinc-400"
                      disabled={!(target in data && data[target])}
                      onClick={prev}
                    >
                      <Icon
                        name="chevron-left"
                        size="sm"
                        className="inline-block"
                      />
                      이전
                    </Button>
                    <Button
                      type="primary"
                      className="flex-auto"
                      disabled={!(target in data && data[target])}
                      onClick={handleDuplicate}
                    >
                      <Icon
                        name="document-duplicate"
                        size="sm"
                        className="inline-block"
                      />
                      복제
                    </Button>
                  </div>
                  <Button
                    className="w-full bg-zinc-300 border-0 text-black hover:bg-zinc-400"
                    onClick={onClose}
                  >
                    <Icon name="x" size="sm" className="inline-block" />
                    닫기
                  </Button>
                </div>
              </div>
            )}
            {page === 1 && mode === "upload" && (
              <div className="card-body overflow-hidden scale-90 m-[-2rem]">
                <div className="text-start h-full border border-zinc-200 rounded-md flex flex-col p-4 gap-4 flex-1">
                  <div className="inline-flex gap-4 items-center">
                    <h3 className="text-sm font-bold w-12 text-end">대상</h3>
                    <div className="btn-group w-full justify-center">
                      <button
                        id="parent"
                        className={`btn btn-sm w-1/2 btn-ghost border border-zinc-200 ${
                          target === "parent" && "btn-active"
                        }`}
                        onClick={handleClick}
                      >
                        학부모
                      </button>
                      <button
                        id="student"
                        className={`btn btn-sm w-1/2 btn-ghost border border-zinc-200 ${
                          target === "student" && "btn-active"
                        }`}
                        onClick={handleClick}
                      >
                        학생
                      </button>
                    </div>
                  </div>
                  <div className="inline-flex gap-4 items-baseline">
                    <h3 className="text-sm font-bold w-12 text-end">파일</h3>
                    <div className="flex flex-col gap-4 w-full">
                      <input
                        type="file"
                        name="excel_upload"
                        accept=".xls,.xlsx"
                        className="file-input file-input-bordered file-input-accent w-full"
                        onChange={handleFileChange}
                        disabled={loading || result}
                      />
                      {!loading && result && (
                        <SuccessAlert>{result}</SuccessAlert>
                      )}
                      {loading && (
                        <progress className="progress progress-primary w-full"></progress>
                      )}
                      {error && <ErrorAlert>{result}</ErrorAlert>}
                    </div>
                  </div>
                </div>
                <div className="card-actions mt-4 w-full">
                  <div className="inline-flex gap-2 w-full">
                    <Button
                      className="flex-auto bg-zinc-300 border-0 text-black hover:bg-zinc-400"
                      disabled={!(target in data && data[target])}
                      onClick={prev}
                    >
                      <Icon
                        name="chevron-left"
                        size="sm"
                        className="inline-block"
                      />
                      이전
                    </Button>
                    <Button
                      type="primary"
                      className="flex-auto"
                      disabled={loading || !!result}
                      onClick={handleUpload}
                      outlined
                    >
                      <Icon
                        name="arrow-up-tray"
                        size="sm"
                        className="inline-block"
                      />
                      업로드
                    </Button>
                  </div>
                  <Button
                    className="w-full bg-zinc-300 border-0 text-black hover:bg-zinc-400"
                    onClick={onClose}
                  >
                    <Icon name="x" size="sm" className="inline-block" />
                    닫기
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default SurveyViewModal;
